/**
 * DedupWorkspace — Phase 10.3 admin workspace for merging duplicate customers.
 *
 * Two modes share the same workspace surface:
 *
 *   Scan  — explicit "Spustit detekci" CTA fires `GET /v1/management/
 *           customers/dedup/groups`. Each group renders as a card with the
 *           full candidate table, a radio for "kanonický", a conflict
 *           resolver dropdown per disagreeing field, and Skip/Merge buttons.
 *   Log   — toggles a read-only `DataTable` of recent merges sourced from
 *           `GET /v1/management/customers/dedup/log`.
 *
 * Merge confirmation uses an inline second-click on the "Sloučit" button
 * (so we keep the work in-page and avoid a separate confirm modal). Post-
 * merge, the group card flips into a green success strip with the counts
 * the API returned, mirroring the legacy semantics; the rest of the
 * groups stay scannable.
 *
 * ARES lookup is intentionally deferred — the legacy page wires it into
 * the conflict-resolver dropdowns, but the dedup payload already exposes
 * every duplicate's existing value for each conflict; admins can always
 * follow up via the customer detail page if ARES override is needed.
 */

import { Link } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { Badge } from '@/components/Badge';
import { Button } from '@/components/Button';
import { DataTable, type DataTableColumn } from '@/components/DataTable';
import { EmptyState } from '@/components/EmptyState';
import { Icon } from '@/components/Icon';
import { Skeleton } from '@/components/Skeleton';
import { Toolband } from '@/components/Toolband';
import { toast } from '@/components/Toaster';
import { ApiError } from '@/lib/api';
import { formatDate, formatInt } from '@/lib/format';
import {
  useDedupGroupsQuery,
  useDedupLogQuery,
  useDedupMergeMutation,
  type DedupConflict,
  type DedupGroup,
  type DedupLogEntry,
} from '@/modules/management/api';

const FIELDS: (keyof DedupConflict['values'][number] | string)[] = [
  'jmeno',
  'ulice',
  'cislo_popisne',
  'psc',
  'mesto',
  'ico',
  'dico',
  'zastupce',
  'telefon',
  'email',
];

const FIELD_LABEL: Record<string, string> = {
  jmeno: 'Jméno',
  ulice: 'Ulice',
  cislo_popisne: 'Č. p.',
  psc: 'PSČ',
  mesto: 'Město',
  ico: 'IČO',
  dico: 'DIČ',
  zastupce: 'Zástupce',
  telefon: 'Telefon',
  email: 'E-mail',
};

type Mode = 'scan' | 'log';

interface DedupWorkspaceProps {
  isAdmin: boolean;
}

export function DedupWorkspace({ isAdmin }: DedupWorkspaceProps) {
  const [mode, setMode] = useState<Mode>('scan');
  const [scanEnabled, setScanEnabled] = useState(false);

  const groups = useDedupGroupsQuery(isAdmin && scanEnabled);
  const log = useDedupLogQuery(isAdmin && mode === 'log');

  if (!isAdmin) {
    return (
      <div className="flex min-h-0 flex-1 flex-col gap-5 overflow-auto px-8 pb-8 pt-5">
        <EmptyState
          eyebrow="Pouze pro admina"
          title="Deduplikace je dostupná pouze administrátorům firmy."
          description="Pokud potřebujete přístup, kontaktujte správce."
        />
      </div>
    );
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
      <Toolband>
        <Button
          variant={mode === 'scan' ? 'ink' : 'secondary'}
          size="sm"
          icon={<Icon name="search" size={12} />}
          onClick={() => {
            setMode('scan');
            setScanEnabled(true);
            groups.refetch();
          }}
          disabled={groups.isFetching && mode === 'scan'}
        >
          {scanEnabled
            ? groups.isFetching
              ? 'Detekuji…'
              : 'Spustit detekci znovu'
            : 'Spustit detekci duplikátů'}
        </Button>
        <Button
          variant={mode === 'log' ? 'ink' : 'secondary'}
          size="sm"
          icon={<Icon name="history" size={12} />}
          onClick={() => setMode('log')}
        >
          Historie sloučení
        </Button>
        <Toolband.Spacer />
        {scanEnabled && groups.data && (
          <span className="font-mono text-[10.5px] uppercase tracking-[0.08em] text-ink-3">
            {formatInt(groups.data.groups.length)} skupin nalezeno
          </span>
        )}
      </Toolband>

      <div
        data-testid="management-dedup"
        className="flex min-h-0 flex-1 flex-col gap-5 overflow-auto px-8 pb-8 pt-5"
      >
        {mode === 'log' ? (
          <LogPane
            entries={log.data?.entries ?? []}
            isLoading={log.isLoading}
            isError={log.isError}
          />
        ) : !scanEnabled ? (
          <EmptyState
            eyebrow="Připraveno k detekci"
            title="Spusťte detekci duplikátů pomocí tlačítka nahoře."
            description="Detekce porovná všechny zákazníky firmy podle IČO, e-mailu a manuálního porovnání jména s telefonem / zástupcem / adresou."
          />
        ) : groups.isLoading ? (
          <div className="flex flex-col gap-3">
            <Skeleton className="h-24 w-full" />
            <Skeleton className="h-24 w-full" />
            <Skeleton className="h-24 w-full" />
          </div>
        ) : groups.isError ? (
          <EmptyState
            eyebrow="Chyba"
            title="Detekce duplikátů selhala."
            description={
              groups.error instanceof Error ? groups.error.message : 'Zkuste to znovu.'
            }
          />
        ) : groups.data && groups.data.groups.length === 0 ? (
          <EmptyState
            eyebrow="Vše v pořádku"
            title="Žádné duplikáty nenalezeny."
            description="Všichni zákazníci ve firmě prošli detekcí bez nálezů."
          />
        ) : (
          <div className="flex flex-col gap-4">
            {groups.data?.groups.map((g, idx) => (
              <GroupCard key={`${g.match_type}:${g.group_key}:${idx}`} group={g} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────
   Per-group card
   ───────────────────────────────────────────────────────────────────────── */

interface GroupCardProps {
  group: DedupGroup;
}

function GroupCard({ group }: GroupCardProps) {
  const merge = useDedupMergeMutation();
  const [canonicalId, setCanonicalId] = useState<number>(group.suggested_canonical_id);
  const [overrides, setOverrides] = useState<Record<string, string>>(() =>
    Object.fromEntries(
      group.conflicts.map((c) => [c.field, c.values[0]?.value ?? '']),
    ),
  );
  const [confirming, setConfirming] = useState(false);
  const [merged, setMerged] = useState<{
    deleted: number;
    repointedTotal: number;
  } | null>(null);
  const [dismissed, setDismissed] = useState(false);

  const duplicateIds = useMemo(
    () => group.customers.filter((c) => c.id_zakaznika !== canonicalId).map((c) => c.id_zakaznika),
    [group.customers, canonicalId],
  );
  const totalChildren = group.customers.reduce(
    (sum, c) => sum + c.child_counts.total,
    0,
  );
  const headline = group.customers[0]?.jmeno || '(bez jména)';

  if (dismissed) return null;

  const onMerge = () => {
    if (!confirming) {
      setConfirming(true);
      return;
    }
    if (duplicateIds.length === 0) {
      toast.warn('Vyberte jiný kanonický záznam — duplikáty by se neměly stát kanonickými.');
      return;
    }
    merge.mutate(
      {
        canonical_id: canonicalId,
        duplicate_ids: duplicateIds,
        field_overrides: overrides,
      },
      {
        onSuccess: (result) => {
          setMerged({
            deleted: result.deleted,
            repointedTotal: result.repointed.total,
          });
          toast.ok(
            `Sloučeno · smazáno ${formatInt(result.deleted)}, přesměrováno ${formatInt(result.repointed.total)}`,
          );
        },
        onError: (err) => {
          const msg =
            err instanceof ApiError && err.body && typeof err.body === 'object'
              ? (err.body as { error?: string }).error ?? 'Sloučení selhalo'
              : err instanceof Error
                ? err.message
                : 'Sloučení selhalo';
          toast.error(msg);
          setConfirming(false);
        },
      },
    );
  };

  return (
    <section
      data-testid="dedup-group"
      data-match-type={group.match_type}
      className="border border-line bg-paper"
    >
      <header className="flex items-center gap-3 border-b border-line px-5 py-3.5">
        <MatchBadge matchType={group.match_type} />
        <strong className="text-[14px] text-ink">{headline}</strong>
        <span className="font-mono text-[10.5px] uppercase tracking-[0.08em] text-ink-3">
          {formatInt(group.customers.length)} duplikátů · {formatInt(totalChildren)}{' '}
          navázaných řádků
        </span>
        <span className="ml-auto" />
        {merged ? (
          <Badge tone="ok">Sloučeno</Badge>
        ) : (
          <>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setDismissed(true)}
            >
              Přeskočit
            </Button>
            <Button
              variant="primary"
              size="sm"
              icon={<Icon name="check" size={12} />}
              onClick={onMerge}
              disabled={merge.isPending}
            >
              {merge.isPending
                ? 'Slučuji…'
                : confirming
                  ? 'Opravdu sloučit?'
                  : `Sloučit (${formatInt(duplicateIds.length)})`}
            </Button>
          </>
        )}
      </header>

      {merged ? (
        <div className="border-b border-line bg-[color:var(--ok-soft)] px-5 py-4 text-[12.5px] text-ink-2">
          Sloučeno — smazáno <b className="font-semibold text-ink">{formatInt(merged.deleted)}</b>{' '}
          duplikátů, přesměrováno{' '}
          <b className="font-semibold text-ink">{formatInt(merged.repointedTotal)}</b>{' '}
          navázaných řádků na zákazníka{' '}
          <Link
            to="/zakaznici/$id"
            params={{ id: String(canonicalId) }}
            className="text-accent-text hover:underline"
          >
            #{canonicalId}
          </Link>
          .
        </div>
      ) : (
        <>
          <CandidateTable
            group={group}
            canonicalId={canonicalId}
            onCanonicalChange={(id) => {
              setCanonicalId(id);
              setConfirming(false);
            }}
          />

          {group.conflicts.length > 0 && (
            <div className="border-t border-line bg-[color:var(--warn-soft)] px-5 py-4">
              <p className="mb-3 font-mono text-[10.5px] uppercase tracking-[0.08em] text-ink-2">
                Konflikty hodnot — vyberte, co zůstane na kanonickém záznamu:
              </p>
              <div className="flex flex-col gap-2">
                {group.conflicts.map((cf) => (
                  <div
                    key={cf.field}
                    className="flex items-center gap-3 text-[12.5px]"
                  >
                    <label
                      htmlFor={`override-${cf.field}`}
                      className="w-28 font-mono text-[10.5px] uppercase tracking-[0.08em] text-ink-2"
                    >
                      {FIELD_LABEL[cf.field] || cf.field}
                    </label>
                    <select
                      id={`override-${cf.field}`}
                      value={overrides[cf.field] ?? ''}
                      onChange={(e) =>
                        setOverrides({ ...overrides, [cf.field]: e.target.value })
                      }
                      className="min-w-[260px] border border-line bg-paper px-2 py-1 font-mono text-[12px] text-ink focus:border-accent focus:outline-none"
                    >
                      {cf.values.map((v) => (
                        <option key={v.id_zakaznika} value={v.value}>
                          {v.value || '(prázdné)'} — #{v.id_zakaznika}
                        </option>
                      ))}
                    </select>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </section>
  );
}

function MatchBadge({ matchType }: { matchType: DedupGroup['match_type'] }) {
  const label = matchType === 'ico' ? 'IČO' : matchType === 'email' ? 'E-mail' : 'Ručně';
  const tone =
    matchType === 'ico' ? 'ok' : matchType === 'email' ? 'warn' : 'neutral';
  return <Badge tone={tone as 'ok' | 'warn' | 'neutral'}>{label}</Badge>;
}

/* ─────────────────────────────────────────────────────────────────────────
   Candidate table
   ───────────────────────────────────────────────────────────────────────── */

interface CandidateTableProps {
  group: DedupGroup;
  canonicalId: number;
  onCanonicalChange: (id: number) => void;
}

function CandidateTable({ group, canonicalId, onCanonicalChange }: CandidateTableProps) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse text-[12px]">
        <thead className="bg-bg">
          <tr className="text-left font-mono text-[10px] uppercase tracking-[0.08em] text-ink-3">
            <th className="w-[80px] px-3 py-2.5">Kanonický</th>
            <th className="w-[80px] px-3 py-2.5">ID</th>
            {FIELDS.map((f) => (
              <th key={f as string} className="px-3 py-2.5">
                {FIELD_LABEL[f as string] || (f as string)}
              </th>
            ))}
            <th className="w-[140px] px-3 py-2.5 text-right">Navázáno</th>
          </tr>
        </thead>
        <tbody>
          {group.customers.map((c) => {
            const checked = c.id_zakaznika === canonicalId;
            return (
              <tr
                key={c.id_zakaznika}
                className={
                  'border-t border-line transition-colors ' +
                  (checked ? 'bg-[color:var(--accent-soft)]' : 'hover:bg-bg')
                }
              >
                <td className="px-3 py-2.5">
                  <input
                    type="radio"
                    name={`canonical-${group.group_key}`}
                    value={c.id_zakaznika}
                    checked={checked}
                    onChange={() => onCanonicalChange(c.id_zakaznika)}
                    aria-label={`Vybrat ${c.jmeno || c.id_zakaznika} jako kanonický`}
                  />
                </td>
                <td className="px-3 py-2.5 font-mono text-[11.5px] tabular-nums tracking-[-0.02em] text-ink-2">
                  #{c.id_zakaznika}
                </td>
                {FIELDS.map((f) => {
                  const v = (c as unknown as Record<string, string>)[f as string] ?? '';
                  return (
                    <td
                      key={f as string}
                      className="px-3 py-2.5 text-ink"
                      title={v || ''}
                    >
                      <span className="block max-w-[180px] truncate">{v || '—'}</span>
                    </td>
                  );
                })}
                <td className="px-3 py-2.5 text-right">
                  <div className="font-mono text-[11.5px] tabular-nums tracking-[-0.02em] text-ink">
                    {formatInt(c.child_counts.total)}
                  </div>
                  {c.child_counts.total > 0 && (
                    <ChildBreakdown counts={c.child_counts as unknown as Record<string, number>} />
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

const CHILD_TABLES = [
  ['vozidlo', 'vozidla'],
  ['vozidla_nabidka', 'voz. nab.'],
  ['vozidla_smlouva', 'voz. sml.'],
  ['ojete_vozidlo', 'ojetá voz.'],
  ['ojeta_vozidla_nabidka', 'oj. nab.'],
  ['ojeta_vozidla_smlouva', 'oj. sml.'],
  ['ojeta_vozidla_vykup_smlouva', 'oj. výkup'],
  ['operak_nabidka', 'op. nab.'],
  ['operak_smlouva', 'op. sml.'],
  ['kontakt', 'kontakty'],
  ['servis_udalost', 'servis'],
  ['pneu_uskladneni_sada', 'pneu skl.'],
  ['pneu_vymena', 'pneu vým.'],
  ['zakaznik_ridic', 'řidiči'],
  ['pujcovna_rezervace', 'rezervace'],
] as const;

function ChildBreakdown({ counts }: { counts: Record<string, number> }) {
  const parts: string[] = [];
  for (const [k, label] of CHILD_TABLES) {
    const n = counts[k] ?? 0;
    if (n > 0) parts.push(`${label}: ${n}`);
  }
  if (parts.length === 0) return null;
  return (
    <div className="mt-1 font-mono text-[10px] tracking-[-0.02em] text-ink-3">
      {parts.join(' · ')}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────
   Historie sloučení (read-only)
   ───────────────────────────────────────────────────────────────────────── */

interface LogPaneProps {
  entries: DedupLogEntry[];
  isLoading: boolean;
  isError: boolean;
}

function LogPane({ entries, isLoading, isError }: LogPaneProps) {
  const cols = useMemo<DataTableColumn<DedupLogEntry>[]>(
    () => [
      {
        id: 'datum',
        header: 'Datum',
        cell: (e) => (
          <span className="font-mono text-[11px] tabular-nums text-ink-2">
            {formatDate(e.datum) || e.datum.slice(0, 10)}
          </span>
        ),
        width: '120px',
      },
      {
        id: 'canonical',
        header: 'Kanonický',
        cell: (e) => (
          <Link
            to="/zakaznici/$id"
            params={{ id: String(e.canonical_id) }}
            className="font-mono text-[11.5px] tabular-nums text-accent-text hover:underline"
          >
            #{e.canonical_id}
          </Link>
        ),
        width: '110px',
      },
      {
        id: 'deleted',
        header: 'Smazaný',
        cell: (e) => (
          <span className="font-mono text-[11.5px] tabular-nums text-ink-2">
            #{e.deleted_id}
          </span>
        ),
        width: '110px',
      },
      {
        id: 'user',
        header: 'Uživatel',
        cell: (e) => (
          <span className="font-mono text-[11.5px] tabular-nums text-ink-2">
            #{e.id_uzivatele}
          </span>
        ),
        width: '110px',
      },
      {
        id: 'repointed',
        header: 'Přesměrováno',
        cell: (e) => {
          const counts = (e.repoint_counts ?? {}) as Record<string, number>;
          return (
            <span className="font-mono text-[11.5px] tabular-nums text-ink">
              {formatInt(counts.total ?? 0)}
            </span>
          );
        },
        width: '130px',
        align: 'right',
      },
      {
        id: 'merge',
        header: 'Merge ID',
        cell: (e) => (
          <span className="font-mono text-[10.5px] uppercase tracking-[0.06em] text-ink-3">
            {e.merge_id.slice(0, 8)}
          </span>
        ),
        width: 'minmax(0, 1fr)',
      },
    ],
    [],
  );

  if (isLoading) {
    return (
      <div className="flex flex-col gap-2">
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
      </div>
    );
  }
  if (isError) {
    return (
      <EmptyState
        eyebrow="Chyba"
        title="Načtení historie selhalo."
        description="Zkuste obnovit stránku."
      />
    );
  }
  if (entries.length === 0) {
    return (
      <EmptyState
        eyebrow="Prázdná historie"
        title="Žádná sloučení dosud neproběhla."
      />
    );
  }
  return (
    <DataTable<DedupLogEntry>
      aria-label="Historie sloučení"
      variant="bordered"
      columns={cols}
      data={entries}
      getRowId={(e) => String(e.id_log)}
    />
  );
}
